import { attemptKey } from '../domain.ts';
import type { Exercise, Progress, Question } from '../types.ts';
import { QuestionCard } from './QuestionCard.tsx';

export function ExercisePanel({
  exercise,
  context,
  progress,
  update,
  onRead,
  onSubmit,
}: {
  exercise: Exercise;
  context: string;
  progress: Progress;
  update: (fn: (value: Progress) => Progress) => void;
  onRead: (question?: Question) => void;
  onSubmit: (question: Question, input: string) => void;
}) {
  const attempted = exercise.questions.filter(
    (question) => progress.attempts[attemptKey(context, question.id)],
  ).length;
  const articleLink = exercise.article && (
    <a className="article-link" href={exercise.article.url} target="_blank" rel="noreferrer">
      Read on {exercise.article?.publisher ?? 'Wikipedia'} ↗
    </a>
  );
  return (
    <>
      {exercise.article && (
        <section className="article-preview" aria-label="Article preview">
          <p>{exercise.article.preview}</p>
          <p className="article-instruction">
            {articleLink} Read only the sections below, including their subsections, then answer
            from memory.
          </p>
          <div className="article-scope">
            <p>
              Assigned reading · about {Math.round(exercise.article.scope.wordCount / 50) * 50}{' '}
              words
            </p>
            <ul>
              {exercise.article.scope.sections.map((section) => (
                <li key={section.url}>
                  <a href={section.url} target="_blank" rel="noreferrer">
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
      <div className="question-toolbar">
        <span className="quiet">
          {attempted} of {exercise.questions.length} attempted
        </span>
      </div>
      <div className="questions">
        {exercise.questions.map((question, index) => (
          <QuestionCard
            key={question.id}
            question={question}
            index={index}
            context={context}
            progress={progress}
            update={update}
            onRead={() => onRead(question)}
            onSubmit={(input) => onSubmit(question, input)}
          />
        ))}
      </div>
      {attempted === exercise.questions.length && (
        <div className="completion" role="status">
          Set complete.
        </div>
      )}
      {!exercise.article && (
        <div className="optional-review">
          <button className="text-button" onClick={() => onRead()}>
            Review concepts & explanations ↗
          </button>
          <p>Optional, after attempting the questions.</p>
        </div>
      )}
    </>
  );
}
